import React from 'react';
import { Card, Skeleton,Empty } from 'antd';
import { ResponsiveLine } from '@nivo/line';
import dayjs from 'dayjs';

const ActivityRateTooltip = ({ point }) => {
  const formattedDate = dayjs(point.data.x).format('YYYY-MM-DD');
  const formattedRate = (point.data.y * 100).toFixed(1) + '%';

  return (
    <div
      style={{
        background: 'white',
        padding: '9px 12px',
        border: '1px solid #ccc',
        borderRadius: '3px',
        boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
        fontSize: '12px',
        whiteSpace: 'nowrap',
      }}
    >
      <div style={{ marginBottom: '4px' }}>{formattedDate}</div>
      <div style={{ fontWeight: 'bold' }}>{formattedRate}</div>
    </div>
  );
};

export default function UserActivityRateCard({ data, dateParams }) {
  const cleaned = (data || [])
    .map((d) => ({ x: d?.date, y: d.dailyActiveUserRate }))
    .filter((p) => typeof p.x === 'string' && p.x && Number.isFinite(p.y));

  // Calculate day difference for better sampling
  const dayDiff = React.useMemo(() => {
    if (!dateParams?.start || !dateParams?.end) return cleaned.length;
    const s = dayjs(dateParams.start);
    const e = dayjs(dateParams.end);
    return Math.max(1, e.diff(s, 'day') + 1);
  }, [dateParams?.start, dateParams?.end, cleaned.length]);

  // sampling: keep all for short ranges (<=7 points), otherwise down-sample based on range
  const sampled = React.useMemo(() => {
    if (cleaned.length <= 7) return cleaned;

    let target = 10;
    if (dayDiff > 365) target = 8; // For year range, show even fewer
    else if (dayDiff > 90) target = 10; // For quarter range

    if (cleaned.length <= target) return cleaned;
    const step = Math.max(1, Math.ceil(cleaned.length / target));
    const arr = cleaned.filter((_, i) => i % step === 0);
    if (arr[arr.length - 1]?.x !== cleaned[cleaned.length - 1]?.x)
      arr.push(cleaned[cleaned.length - 1]);
    return arr;
  }, [cleaned, dayDiff]);
  const series = sampled.length >= 2 ? [{ id: 'activity', data: sampled }] : [];
  return (
    <Card
      title={
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          User Activity Rate
        </span>
      }
    >
      {!data ? (
        <Skeleton active />
      ) : series.length === 0 ? (
        <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : (
        <div style={{ height: 240 }}>
          <ResponsiveLine
            data={series}
            margin={{ top: 10, right: 20, bottom: 40, left: 40 }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              stacked: false,
              nice: true,
              min: 0,
              max: 1,
            }}
            curve="monotoneX"
            colors={['#69b1ff']}
            enablePoints={false}
            pointSize={0}
            axisBottom={{
              tickRotation: dayDiff > 30 ? -45 : 0,
              format: (v) => {
                if (dayDiff > 365) return dayjs(v).format('MMM');
                else if (dayDiff > 90) return dayjs(v).format('MM-DD');
                else return dayjs(v).format('MM-DD');
              },
              legend: '',
              legendOffset: 32,
            }}
            axisLeft={{
              tickValues: [0, 0.25, 0.5, 0.75, 1.0],
              legend: '',
              legendOffset: -32,
              format: (v) => `${v * 100}%`,
            }}
            tooltip={ActivityRateTooltip}
            enableGridX={false}
            enableGridY={false}
            theme={{
              axis: {
                domain: { line: { stroke: '#d9d9d9', strokeWidth: 1 } },
                ticks: { line: { stroke: '#d9d9d9', strokeWidth: 1 } },
              },
            }}
            useMesh
          />
        </div>
      )}
    </Card>
  );
}
